import React, { useState, useEffect } from 'react';
import { Menu, X, Leaf, LogIn, User, Heart, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { siteConfig, navLinks } from '../data/mock';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { useNavigate, useLocation } from 'react-router-dom';

import CurrencySelector from './CurrencySelector';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { basketCount } = useBasket();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    if (link.isPage) {
      navigate(link.href);
    }
    setIsMobileMenuOpen(false);
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const handleDonateClick = () => {
    navigate('/donate');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Title with tagline UNDERNEATH */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <img
              src={siteConfig.logo}
              alt={siteConfig.name}
              className="h-10 w-10 lg:h-12 lg:w-12 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden md:block">
              <h1 className="font-serif font-bold text-base lg:text-lg text-slate-800 whitespace-nowrap leading-tight">
                {siteConfig.name}
              </h1>
              <p className="text-xs lg:text-sm text-emerald-600 flex items-center gap-1 whitespace-nowrap">
                <Leaf className="w-3 h-3" />
                {siteConfig.tagline}
              </p>
            </div>
          </a>

          {/* Desktop Navigation - BIGGER text */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  isActive(link.href)
                    ? 'bg-emerald-100 text-emerald-700'
                    : isScrolled
                      ? 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                      : 'text-slate-700 hover:text-emerald-600 hover:bg-white/50'
                }`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Basket icon - visible on all screens */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => navigate('/shop')}
              className="relative p-2 text-slate-600 hover:text-amber-600 transition-colors"
              aria-label="Shopping basket"
            >
              <ShoppingCart className="w-5 h-5" />
              {basketCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {basketCount}
                </span>
              )}
            </button>
          </div>

          {/* Auth Buttons - desktop only */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <CurrencySelector />
            {isAuthenticated ? (
              <>
                <Button
                  onClick={handleDonateClick}
                  size="sm"
                  className="bg-coral-500 hover:bg-coral-600 text-white rounded-full px-4 h-9 text-sm shadow-lg shadow-coral-500/25 transition-all duration-300 hover:shadow-xl"
                >
                  <Heart className="w-4 h-4 mr-1.5" />
                  Donate
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 h-9 text-sm shadow-lg shadow-emerald-500/25 transition-all duration-300 hover:shadow-xl group"
                >
                  <User className="w-4 h-4 mr-1.5" />
                  My Library
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  size="sm"
                  className="rounded-full px-3 h-9 text-sm text-slate-600 hover:text-emerald-600 hover:bg-emerald-50"
                >
                  <LogIn className="w-4 h-4 mr-1.5" />
                  Sign In
                </Button>
                <Button
                  onClick={handleDonateClick}
                  size="sm"
                  className="bg-coral-500 hover:bg-coral-600 text-white rounded-full px-4 h-9 text-sm shadow-lg shadow-coral-500/25 transition-all duration-300 hover:shadow-xl"
                >
                  <Heart className="w-4 h-4 mr-1.5" />
                  Donate
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-emerald-50 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link);
                  }}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-emerald-600'
                  }`}
                >
                  {link.name}
                </a>
              ))}
              
              <div className="pt-4 border-t border-slate-100 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Button
                      onClick={() => { navigate('/dashboard'); setIsMobileMenuOpen(false); }}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-3"
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Library
                    </Button>
                    <Button
                      onClick={() => { handleDonateClick(); setIsMobileMenuOpen(false); }}
                      className="w-full bg-coral-500 hover:bg-coral-600 text-white rounded-xl py-3"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Donate
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}
                      variant="outline"
                      className="w-full rounded-xl py-3"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </Button>
                    <Button
                      onClick={() => { handleDonateClick(); setIsMobileMenuOpen(false); }}
                      className="w-full bg-coral-500 hover:bg-coral-600 text-white rounded-xl py-3"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Donate
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
